/**
 * Pilot Profile Settings Component
 * User-friendly settings page with profile details, address, image upload, and vehicle info
 */

import React, { useState, useRef } from 'react';
import './PilotProfileSettings.css';

const PilotProfileSettings = ({ 
  user, 
  token, 
  apiBaseUrl, 
  onClose, 
  onSave 
}) => {
  const fileInputRef = useRef(null);
  const [activeTab, setActiveTab] = useState('personal'); // personal, address, vehicle, security
  const [isSaving, setIsSaving] = useState(false);
  const [profileImage, setProfileImage] = useState(user?.profile_image_url || null);
  const [imagePreview, setImagePreview] = useState(user?.profile_image_url || null);

  // Personal Info
  const [personalInfo, setPersonalInfo] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    date_of_birth: user?.date_of_birth || '',
  });

  // Address Info
  const [addressInfo, setAddressInfo] = useState({
    street_address: user?.street_address || '',
    city: user?.city || '',
    state_province: user?.state_province || '',
    postal_code: user?.postal_code || '',
    country: user?.country || 'Nigeria',
  });

  // Vehicle Info
  const [vehicleInfo, setVehicleInfo] = useState({
    vehicle_type: user?.vehicle_type || 'motorcycle',
    vehicle_make: user?.vehicle_make || '',
    vehicle_model: user?.vehicle_model || '',
    vehicle_year: user?.vehicle_year || new Date().getFullYear(),
    license_plate: user?.license_plate || '',
    insurance_provider: user?.insurance_provider || '',
    insurance_expiry: user?.insurance_expiry || '',
  });

  // Security
  const [securityInfo, setSecurityInfo] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  const [message, setMessage] = useState({ type: '', text: '' });

  // Handle image upload
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setMessage({ type: 'error', text: 'Image size must be less than 5MB' });
        return;
      }

      // Validate file type
      if (!file.type.startsWith('image/')) {
        setMessage({ type: 'error', text: 'Please select a valid image file' });
        return;
      }

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
        setProfileImage(file);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle personal info changes
  const handlePersonalChange = (e) => {
    const { name, value } = e.target;
    setPersonalInfo(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle address info changes
  const handleAddressChange = (e) => {
    const { name, value } = e.target;
    setAddressInfo(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle vehicle info changes
  const handleVehicleChange = (e) => {
    const { name, value } = e.target;
    setVehicleInfo(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle security info changes
  const handleSecurityChange = (e) => {
    const { name, value } = e.target;
    setSecurityInfo(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Save personal info
  const handleSavePersonal = async () => {
    setIsSaving(true);
    try {
      const formData = new FormData();
      formData.append('first_name', personalInfo.first_name);
      formData.append('last_name', personalInfo.last_name);
      formData.append('email', personalInfo.email);
      formData.append('phone', personalInfo.phone);
      if (personalInfo.date_of_birth) {
        formData.append('date_of_birth', personalInfo.date_of_birth);
      }

      // Add image if new one was selected
      if (profileImage instanceof File) {
        formData.append('profile_image', profileImage);
      }

      const response = await fetch(`${apiBaseUrl}/api/v1/pilots/profile`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Personal information saved successfully!' });
        if (onSave) {
          onSave();
        }
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.message || 'Failed to save personal information' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Error saving personal information' });
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  // Save address info
  const handleSaveAddress = async () => {
    setIsSaving(true);
    try {
      const response = await fetch(`${apiBaseUrl}/api/v1/pilots/address`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(addressInfo),
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Address saved successfully!' });
        if (onSave) {
          onSave();
        }
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.message || 'Failed to save address' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Error saving address' });
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  // Save vehicle info
  const handleSaveVehicle = async () => {
    setIsSaving(true);
    try {
      const response = await fetch(`${apiBaseUrl}/api/v1/pilots/vehicle`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(vehicleInfo),
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Vehicle information saved successfully!' });
        if (onSave) {
          onSave();
        }
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.message || 'Failed to save vehicle information' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Error saving vehicle information' });
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  // Change password
  const handleChangePassword = async () => {
    if (securityInfo.newPassword !== securityInfo.confirmPassword) {
      setMessage({ type: 'error', text: 'New passwords do not match' });
      return;
    }

    if (securityInfo.newPassword.length < 8) {
      setMessage({ type: 'error', text: 'Password must be at least 8 characters' });
      return;
    }

    setIsSaving(true);
    try {
      const response = await fetch(`${apiBaseUrl}/api/v1/pilots/change-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          current_password: securityInfo.currentPassword,
          new_password: securityInfo.newPassword,
        }),
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Password changed successfully!' });
        setSecurityInfo({
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
        });
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.message || 'Failed to change password' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Error changing password' });
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="pilot-profile-settings">
      <div className="settings-container">
        {/* Header */}
        <div className="settings-header">
          <h1>⚙️ Profile Settings</h1>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

        {/* Message Alert */}
        {message.text && (
          <div className={`message-alert ${message.type}`}>
            <span>{message.text}</span>
            <button onClick={() => setMessage({ type: '', text: '' })}>✕</button>
          </div>
        )}

        {/* Tabs */}
        <div className="settings-tabs">
          <button 
            className={`tab-btn ${activeTab === 'personal' ? 'active' : ''}`}
            onClick={() => setActiveTab('personal')}
          >
            👤 Personal
          </button>
          <button 
            className={`tab-btn ${activeTab === 'address' ? 'active' : ''}`}
            onClick={() => setActiveTab('address')}
          >
            📍 Address
          </button>
          <button 
            className={`tab-btn ${activeTab === 'vehicle' ? 'active' : ''}`}
            onClick={() => setActiveTab('vehicle')}
          >
            🚗 Vehicle
          </button>
          <button 
            className={`tab-btn ${activeTab === 'security' ? 'active' : ''}`}
            onClick={() => setActiveTab('security')}
          >
            🔒 Security
          </button>
        </div>

        {/* Content */}
        <div className="settings-content">
          {/* Personal Tab */}
          {activeTab === 'personal' && (
            <div className="tab-content">
              <h2>Personal Information</h2>
              
              {/* Profile Image Section */}
              <div className="image-upload-section">
                <div className="image-preview-container">
                  {imagePreview ? (
                    <img src={imagePreview} alt="Profile" className="profile-image-preview" />
                  ) : (
                    <div className="image-placeholder">
                      <span className="placeholder-icon">📸</span>
                      <p>No photo</p>
                    </div>
                  )}
                </div>
                <div className="image-upload-controls">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden-input"
                  />
                  <button 
                    className="btn btn-primary"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    📷 Upload Photo
                  </button>
                  {imagePreview && (
                    <button 
                      className="btn btn-secondary"
                      onClick={() => {
                        setImagePreview(null);
                        setProfileImage(null);
                      }}
                    >
                      🗑️ Remove Photo
                    </button>
                  )}
                  <p className="image-help-text">Max 5MB • JPG, PNG, GIF supported</p>
                </div>
              </div>

              {/* Personal Info Fields */}
              <div className="form-section">
                <div className="form-group">
                  <label>First Name *</label>
                  <input
                    type="text"
                    name="first_name"
                    value={personalInfo.first_name}
                    onChange={handlePersonalChange}
                    placeholder="Enter first name"
                  />
                </div>
                <div className="form-group">
                  <label>Last Name *</label>
                  <input
                    type="text"
                    name="last_name"
                    value={personalInfo.last_name}
                    onChange={handlePersonalChange}
                    placeholder="Enter last name"
                  />
                </div>
                <div className="form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={personalInfo.email}
                    onChange={handlePersonalChange}
                    placeholder="Enter email address"
                  />
                </div>
                <div className="form-group">
                  <label>Phone Number *</label>
                  <input
                    type="tel"
                    name="phone"
                    value={personalInfo.phone}
                    onChange={handlePersonalChange}
                    placeholder="Enter phone number"
                  />
                </div>
                <div className="form-group">
                  <label>Date of Birth</label>
                  <input
                    type="date"
                    name="date_of_birth"
                    value={personalInfo.date_of_birth}
                    onChange={handlePersonalChange}
                  />
                </div>
              </div>

              <button 
                className="btn btn-primary btn-save"
                onClick={handleSavePersonal}
                disabled={isSaving}
              >
                {isSaving ? '⏳ Saving...' : '✓ Save Personal Info'}
              </button>
            </div>
          )}

          {/* Address Tab */}
          {activeTab === 'address' && (
            <div className="tab-content">
              <h2>Address Information</h2>
              <div className="form-section">
                <div className="form-group">
                  <label>Street Address *</label>
                  <input
                    type="text"
                    name="street_address"
                    value={addressInfo.street_address}
                    onChange={handleAddressChange}
                    placeholder="Enter street address"
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>City *</label>
                    <input
                      type="text"
                      name="city"
                      value={addressInfo.city}
                      onChange={handleAddressChange}
                      placeholder="Enter city"
                    />
                  </div>
                  <div className="form-group">
                    <label>State/Province</label>
                    <input
                      type="text"
                      name="state_province"
                      value={addressInfo.state_province}
                      onChange={handleAddressChange}
                      placeholder="Enter state/province"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Postal Code *</label>
                    <input
                      type="text"
                      name="postal_code"
                      value={addressInfo.postal_code}
                      onChange={handleAddressChange}
                      placeholder="Enter postal code"
                    />
                  </div>
                  <div className="form-group">
                    <label>Country *</label>
                    <select
                      name="country"
                      value={addressInfo.country}
                      onChange={handleAddressChange}
                    >
                      <option value="Nigeria">🇳🇬 Nigeria</option>
                      <option value="United Kingdom">🇬🇧 United Kingdom</option>
                      <option value="United States">🇺🇸 United States</option>
                      <option value="Canada">🇨🇦 Canada</option>
                      <option value="Ghana">🇬🇭 Ghana</option>
                      <option value="Kenya">🇰🇪 Kenya</option>
                      <option value="South Africa">🇿🇦 South Africa</option>
                      <option value="Other">🌍 Other</option>
                    </select>
                  </div>
                </div>
              </div>

              <button 
                className="btn btn-primary btn-save"
                onClick={handleSaveAddress}
                disabled={isSaving}
              >
                {isSaving ? '⏳ Saving...' : '✓ Save Address'}
              </button>
            </div>
          )}

          {/* Vehicle Tab */}
          {activeTab === 'vehicle' && (
            <div className="tab-content">
              <h2>Vehicle Information</h2>
              <div className="form-section">
                <div className="form-group">
                  <label>Vehicle Type *</label>
                  <select
                    name="vehicle_type"
                    value={vehicleInfo.vehicle_type}
                    onChange={handleVehicleChange}
                  >
                    <option value="motorcycle">🏍️ Motorcycle</option>
                    <option value="car">🚗 Car</option>
                    <option value="van">🚐 Van</option>
                    <option value="truck">🚚 Truck</option>
                    <option value="bicycle">🚴 Bicycle</option>
                  </select>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Make/Brand *</label>
                    <input
                      type="text"
                      name="vehicle_make"
                      value={vehicleInfo.vehicle_make}
                      onChange={handleVehicleChange}
                      placeholder="e.g., Honda, Toyota"
                    />
                  </div>
                  <div className="form-group">
                    <label>Model *</label>
                    <input
                      type="text"
                      name="vehicle_model"
                      value={vehicleInfo.vehicle_model}
                      onChange={handleVehicleChange}
                      placeholder="e.g., Civic, Camry"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Year *</label>
                    <input
                      type="number"
                      name="vehicle_year"
                      value={vehicleInfo.vehicle_year}
                      onChange={handleVehicleChange}
                      min="1990"
                      max={new Date().getFullYear() + 1}
                    />
                  </div>
                  <div className="form-group">
                    <label>License Plate *</label>
                    <input
                      type="text"
                      name="license_plate"
                      value={vehicleInfo.license_plate}
                      onChange={handleVehicleChange}
                      placeholder="e.g., ABC 123 XY"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Insurance Provider</label>
                    <input
                      type="text"
                      name="insurance_provider"
                      value={vehicleInfo.insurance_provider}
                      onChange={handleVehicleChange}
                      placeholder="Insurance company name"
                    />
                  </div>
                  <div className="form-group">
                    <label>Insurance Expiry</label>
                    <input
                      type="date"
                      name="insurance_expiry"
                      value={vehicleInfo.insurance_expiry}
                      onChange={handleVehicleChange}
                    />
                  </div>
                </div>
              </div>

              <button 
                className="btn btn-primary btn-save"
                onClick={handleSaveVehicle}
                disabled={isSaving}
              >
                {isSaving ? '⏳ Saving...' : '✓ Save Vehicle Info'}
              </button>
            </div>
          )}

          {/* Security Tab */}
          {activeTab === 'security' && (
            <div className="tab-content">
              <h2>Security & Password</h2>
              <div className="form-section">
                <div className="form-group">
                  <label>Current Password *</label>
                  <div className="password-input-wrapper">
                    <input
                      type={showPasswords.current ? 'text' : 'password'}
                      name="currentPassword"
                      value={securityInfo.currentPassword}
                      onChange={handleSecurityChange}
                      placeholder="Enter current password"
                    />
                    <button
                      type="button"
                      className="toggle-password"
                      onClick={() => setShowPasswords(prev => ({ ...prev, current: !prev.current }))}
                    >
                      {showPasswords.current ? '🙈' : '👁️'}
                    </button>
                  </div>
                </div>
                <div className="form-group">
                  <label>New Password *</label>
                  <div className="password-input-wrapper">
                    <input
                      type={showPasswords.new ? 'text' : 'password'}
                      name="newPassword"
                      value={securityInfo.newPassword}
                      onChange={handleSecurityChange}
                      placeholder="Enter new password (min 8 characters)"
                    />
                    <button
                      type="button"
                      className="toggle-password"
                      onClick={() => setShowPasswords(prev => ({ ...prev, new: !prev.new }))}
                    >
                      {showPasswords.new ? '🙈' : '👁️'}
                    </button>
                  </div>
                </div>
                <div className="form-group">
                  <label>Confirm New Password *</label>
                  <div className="password-input-wrapper">
                    <input
                      type={showPasswords.confirm ? 'text' : 'password'}
                      name="confirmPassword"
                      value={securityInfo.confirmPassword}
                      onChange={handleSecurityChange}
                      placeholder="Confirm new password"
                    />
                    <button
                      type="button"
                      className="toggle-password"
                      onClick={() => setShowPasswords(prev => ({ ...prev, confirm: !prev.confirm }))}
                    >
                      {showPasswords.confirm ? '🙈' : '👁️'}
                    </button>
                  </div>
                </div>
              </div>

              <button 
                className="btn btn-primary btn-save"
                onClick={handleChangePassword}
                disabled={isSaving || !securityInfo.currentPassword || !securityInfo.newPassword}
              >
                {isSaving ? '⏳ Updating...' : '✓ Change Password'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PilotProfileSettings;
